// NotificationsPage — In-app notification center
// Layout (matches base44 design language):
//   - Top nav: < Back | Notifications | "Mark all read" action
//   - Filter pills: All / Bot / Challenge / System
//   - Notification list with unread dots, icons, timestamps
//   - Empty state

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Notif {
  id: string;
  kind: 'bot' | 'challenge' | 'system' | 'reward' | 'payment';
  title: string;
  body: string;
  time: string;       // ISO or relative
  unread: boolean;
  action?: { label: string; href: string };
  iconBg: string;     // tailwind color
  icon: string;       // emoji
}

const DEMO_NOTIFS: Notif[] = [
  {
    id: '1', kind: 'bot', unread: true,
    title: 'AI Bot opened a new trade',
    body: 'Breakout Bot entered ES long at 5280.25. Position size: 0.5% of account.',
    time: '2 min ago', iconBg: 'bg-[#34C759]', icon: '🚀',
    action: { label: 'View trade', href: '/portal/bot' },
  },
  {
    id: '2', kind: 'bot', unread: true,
    title: 'Take profit hit on NQ',
    body: 'Breakout Bot closed NQ short at 18,410.50 for +$95.00 (+0.05%).',
    time: '15 min ago', iconBg: 'bg-[#34C759]', icon: '✅',
    action: { label: 'View trade', href: '/portal/bot' },
  },
  {
    id: '3', kind: 'challenge', unread: true,
    title: 'Daily loss at 30% of limit',
    body: 'You\'re at $300 of your $1,000 daily loss limit on the Beginner $50K challenge. Trade carefully.',
    time: '1 hr ago', iconBg: 'bg-[#FF9500]', icon: '⚠️',
    action: { label: 'View challenge', href: '/portal/challenge/ch-1' },
  },
  {
    id: '4', kind: 'challenge', unread: false,
    title: 'Challenge activated',
    body: 'Beginner $50K challenge on Deepchart® is now active. Good luck!',
    time: '3 days ago', iconBg: 'bg-[#34C759]', icon: '🎯',
    action: { label: 'View challenge', href: '/portal/challenge/ch-1' },
  },
  {
    id: '5', kind: 'payment', unread: false,
    title: 'Payment received',
    body: 'We received $149.00 for the Beginner $100K challenge. Order ord-2 confirmed.',
    time: '3 days ago', iconBg: 'bg-tf-blue', icon: '💳',
    action: { label: 'View receipt', href: '/portal/orders' },
  },
  {
    id: '6', kind: 'reward', unread: false,
    title: 'Claim your $30 platform bonus',
    body: 'You have a $30 platform activation bonus expiring in 1d 23h. Claim now.',
    time: '5 days ago', iconBg: 'bg-[#FF9500]', icon: '🎁',
    action: { label: 'Claim now', href: '/portal/rewards' },
  },
  {
    id: '7', kind: 'system', unread: false,
    title: 'Welcome to Funder Pro',
    body: 'Your account is set up. Activate a challenge to get started.',
    time: '1 week ago', iconBg: 'bg-tf-text-1', icon: '👋',
  },
];

const FILTERS = ['All', 'Bot', 'Challenge', 'System'] as const;
type Filter = (typeof FILTERS)[number];

export function NotificationsPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>('All');
  const [notifs, setNotifs] = useState(DEMO_NOTIFS);

  const filtered = notifs.filter((n) => filter === 'All' || n.kind.toLowerCase() === filter.toLowerCase());
  const unreadCount = notifs.filter((n) => n.unread).length;

  function markAll() {
    setNotifs((curr) => curr.map((n) => ({ ...n, unread: false })));
  }

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Notifications</div>
        <button onClick={markAll} disabled={unreadCount === 0} className="px-3 text-[14px] text-tf-blue disabled:opacity-30">
          Mark all
        </button>
      </div>

      {/* Unread summary */}
      <div className="px-4 pt-4">
        <div className="text-[13px] text-tf-text-3">
          {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'All caught up'}
        </div>
      </div>

      {/* Filter pills */}
      <div className="px-4 pt-3 flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`notif-filter-${f.toLowerCase()}`}
            className={['rounded-full px-4 py-1.5 text-[13px] font-semibold transition',
              f === filter ? 'bg-tf-text-1 text-white' : 'bg-tf-surface-1 text-tf-text-1'].join(' ')}
          >
            {f}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-3">
        {filtered.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <div className="text-[14px] text-tf-text-3">No {filter.toLowerCase()} notifications</div>
          </div>
        ) : (
          filtered.map((n, i) => (
            <button
              key={n.id}
              onClick={() => {
                if (n.unread) setNotifs((curr) => curr.map((x) => x.id === n.id ? { ...x, unread: false } : x));
                if (n.action) nav(n.action.href);
              }}
              data-testid={`notif-${n.id}`}
              className={['flex w-full items-start gap-3 border-b border-tf-border px-4 py-3 text-left active:bg-tf-surface-1',
                n.unread ? 'bg-tf-blue-tint' : 'bg-white',
                i === filtered.length - 1 ? 'border-b-0' : ''].join(' ')}
            >
              <div className={['flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[18px]', n.iconBg].join(' ')}>
                {n.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {n.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-tf-blue" />}
                  <span className={['text-[14px] truncate', n.unread ? 'font-semibold text-tf-text-1' : 'font-medium text-tf-text-1'].join(' ')}>{n.title}</span>
                </div>
                <div className="mt-0.5 text-[13px] leading-[1.35] text-tf-text-3">{n.body}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-tf-text-3">
                  <span>{n.time}</span>
                  <span className="rounded-full bg-tf-surface-1 px-2 py-0.5 capitalize">{n.kind}</span>
                </div>
              </div>
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
